import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import CheckBox from '../../shared/TailwindComponents/CheckBox';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import Input from '../../shared/TailwindComponents/Input';
import apis from '../../../utils/apis';
import { formatRoleNameForDisplay } from '../../../utils/roleFormatter';

interface User {
  id: number;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_org_admin: boolean;
  is_active: boolean;
  created_at: string;
}

interface AdminUserModalProps {
  user: User;
  adminCount: number;
  onConfirm: (userData: any) => void;
  onCancel: () => void;
}

const AdminUserModal: React.FC<AdminUserModalProps> = ({
  user,
  adminCount,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [selectedRole, setSelectedRole] = useState(user.role || '');
  const [isOrgAdmin, setIsOrgAdmin] = useState(user.is_org_admin);
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      setIsLoadingRoles(true);
      const response = await apis.get('/admin/roles');
      if (response.data.success) {
        const lawFirmRoles = response.data.data.filter((role: any) => {
          const roleName = role.name.toLowerCase();
          const excludedRoles = ['client', 'external', 'guest'];
          return !excludedRoles.some((excluded) => roleName.includes(excluded));
        });
        const roleOptions = lawFirmRoles.map((role: any) => ({
          value: role.name,
          label: formatRoleNameForDisplay(role.name),
        }));
        setRoles(roleOptions);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setIsLoadingRoles(false);
    }
  }, []);

  const handleSaveUser = () => {
    onConfirm({
      role: selectedRole,
      is_org_admin: isOrgAdmin,
    });
  };

  const isCurrentUserLastAdmin =
    user.id === userInfo?.UserUID && adminCount === 1;

  return (
    <div className="min-w-[400px] sm:w-full">
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <Input
          label={t('Email')}
          type="email"
          value={user.email}
          disabled
        />

        {isLoadingRoles ? (
          <div className="text-center py-4 text-fg-muted">
            {t('Loading roles...')}
          </div>
        ) : (
          <Dropdown
            label={t('Role')}
            options={roles}
            value={selectedRole}
            onChange={(value: string | string[]) =>
              setSelectedRole(typeof value === 'string' ? value : Array.isArray(value) ? value[0] : '')
            }
            fullWidth
          />
        )}

        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
          <CheckBox
            id="adminCheck"
            label={t('Organization Admin')}
            translate={false}
            checked={isOrgAdmin}
            onChange={(e) => setIsOrgAdmin(e.target.checked)}
            disabled={isCurrentUserLastAdmin || isLoadingRoles}
          />
        </div>

        {isCurrentUserLastAdmin && (
          <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <p className="text-xs text-warning-800 dark:text-warning-200">
              {t('You cannot remove admin privileges from yourself if you are the only admin')}
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex gap-3 justify-end px-6 py-4">
        <Button
          variant="neutral"
          size="small"
          onClick={onCancel}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSaveUser}
          disabled={isLoadingRoles}
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default AdminUserModal;
